import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBrands } from '../contexts/BrandContext'
import { useRegisterEditSession } from '../contexts/EditSessionContext'
import { VcardEditorView } from '../components/VcardEditorView'
import { Forbidden } from '../components/common/Forbidden'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { isForbidden } from '../utils/httpError'
import { createVcard, loadVcard, saveVcard } from '../services/vcardStore'
import { notifySuccess, notifyError } from '../utils/notify'
import { savedToast } from '../utils/vcardSave'
import type { Vcard, VcardInput } from '../types'

type LoadStatus = 'loading' | 'ready' | 'forbidden' | 'notfound'

const VCARD_LIST_ROUTE = '/admin/vcards'

/** Leere vCard für den Create-Mode (Spec §5.6: Admin legt vCard + Brand-Wahl an). */
function blankVcard(brandKey: string): Vcard {
  return {
    urlKey: '',
    brandKey,
    active: true,
    teams: {
      id: '',
      name: '',
      logoUrl: '',
      address: { street: '', houseNumber: '', zip: '', city: '' },
      openingHours: [{ open: { day: 1, time: '' }, close: { day: 1, time: '' } }],
    },
  }
}

/** Editor für eine beliebige (erlaubte) vCard — admin & brand-admin (Spec §5.6). */
export function VcardEditorPage() {
  const { urlKey } = useParams<{ urlKey: string }>()
  const isNew = urlKey === 'new'
  const { session } = useAuth()
  const { brands, resolveBrand } = useBrands()
  const navigate = useNavigate()
  const location = useLocation()
  const [vcard, setVcard] = useState<Vcard | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [saving, setSaving] = useState(false)
  const [brandKey, setBrandKey] = useState('')

  // Rücksprungziel: Herkunftspfad (aus der Liste gemerkt) sonst Default-Liste.
  const backTo = (location.state as { from?: string } | null)?.from ?? VCARD_LIST_ROUTE

  // Zielnutzer-Label der geladenen (fremden) vCard für den Edit-on-behalf-Banner.
  const targetLabel = vcard
    ? `${vcard.firstName ?? ''} ${vcard.lastName ?? ''}`.trim() || vcard.urlKey
    : ''

  // Edit-on-behalf-Kontext ans Layout melden — nur bei einer geladenen fremden
  // vCard (nicht im Create-Mode / nicht während des Ladens).
  useRegisterEditSession(status === 'ready' && !isNew && vcard ? { targetLabel, backTo } : null)

  useEffect(() => {
    if (!brandKey && brands.length > 0) setBrandKey(brands[0].brandKey)
  }, [brands, brandKey])

  useEffect(() => {
    if (!urlKey || !session || isNew) return
    let cancelled = false
    setStatus('loading')
    loadVcard(urlKey)
      .then((vc) => {
        if (cancelled) return
        if (!can(session).vcardEdit(vc)) {
          setStatus('forbidden')
          return
        }
        setVcard(vc)
        setStatus('ready')
      })
      .catch((err) => {
        if (!cancelled) setStatus(isForbidden(err) ? 'forbidden' : 'notfound')
      })
    return () => {
      cancelled = true
    }
  }, [urlKey, session, isNew])

  const handleSave = useCallback(
    async (payload: Parameters<typeof saveVcard>[1], cleared: string[]) => {
      if (!urlKey) return
      setSaving(true)
      try {
        await saveVcard(urlKey, payload)
        notifySuccess(savedToast(cleared.length))
        navigate(VCARD_LIST_ROUTE)
      } catch {
        notifyError('Speichern fehlgeschlagen, bitte erneut.')
      } finally {
        setSaving(false)
      }
    },
    [urlKey, navigate],
  )

  const handleCreate = useCallback(
    async (payload: VcardInput) => {
      setSaving(true)
      try {
        await createVcard(payload)
        notifySuccess('vCard angelegt — Nutzerkonto E-Mail = Login')
        navigate(VCARD_LIST_ROUTE)
      } catch {
        notifyError('Anlegen fehlgeschlagen, bitte erneut.')
      } finally {
        setSaving(false)
      }
    },
    [navigate],
  )

  if (!session) return null

  if (isNew) {
    if (!can(session).vcardCreate()) return <Forbidden message="Du darfst keine vCards anlegen." />

    return (
      <>
        <div className="ctxbar">
          <span className="[&_svg]:size-4">{Icons.lock}</span>
          Beim Speichern entsteht automatisch das Nutzerkonto (E-Mail = Login).
        </div>
        <div className="field mb-5" style={{ maxWidth: '320px' }}>
          <label className="flabel" htmlFor="new-vcard-brand">
            Brand
          </label>
          <select
            id="new-vcard-brand"
            className="inp"
            value={brandKey}
            onChange={(e) => setBrandKey(e.target.value)}
          >
            {brands.map((b) => (
              <option key={b.brandKey} value={b.brandKey}>
                {b.brandName}
              </option>
            ))}
          </select>
        </div>
        <VcardEditorView
          key={brandKey}
          vcard={blankVcard(brandKey)}
          brand={resolveBrand(brandKey)}
          scopeHint="Neue vCard — Admin."
          showBack
          backTo={VCARD_LIST_ROUTE}
          saving={saving}
          onSave={handleCreate}
        />
      </>
    )
  }

  if (status === 'loading') return <StatePanel title="vCard-Editor" text="vCard wird geladen …" />
  if (status === 'forbidden') return <Forbidden message="Diese vCard darfst du nicht bearbeiten." />
  if (status === 'notfound' || !vcard) return <StatePanel title="Nicht gefunden" text="vCard nicht gefunden." />

  const isAdmin = session.role === 'admin'
  return (
    <VcardEditorView
      vcard={vcard}
      brand={resolveBrand(vcard.brandKey)}
      scopeHint={isAdmin ? 'Admin darf jede vCard bearbeiten.' : 'Brand-Admin: nur vCards der eigenen Brand.'}
      showBack
      backTo={backTo}
      saving={saving}
      onSave={handleSave}
    />
  )
}

/** Lade-/Nicht-gefunden-Zustand als `.panel` mit `.empty` — BMC-Designsystem `console.css`. */
function StatePanel({ title, text }: { title: string; text: string }) {
  return (
    <section className="panel">
      <div className="head">
        <h2>{title}</h2>
      </div>
      <div className="empty">{text}</div>
    </section>
  )
}
